import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import { PayPage } from 'src/pages/payments/pay-page/pay-page';
import { LeaseLedgerPage } from 'src/pages/leases/lease-ledger/lease-ledger';

import { RentCollectionService, Payment, PaymentStatus } from 'src/shared/services/rent-collection.service';

@Component({
  templateUrl: 'payment-detail.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PaymentDetailPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private featureGate = inject(FeatureGateService);
  private rentService = inject(RentCollectionService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  payment: Payment;
  loaded = false;
  private panelClosedSubscription: Subscription;

  // Precomputed display strings for room/cadence details — no function calls in the template.
  propertyLabel = '';
  cadenceLabel = '';
  leaseLabel = '';
  statusColorValue = '';

  copyAction = () => this.copyLink();
  sendAction = () => this.sendViaMessages();
  remindAction = () => this.sendReminder();
  previewAction = () => this.openPayPage();
  recordOfflineAction = () => this.recordOffline();
  goToLeaseAction = () => this.goToLease();

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const params = await this.storage.retrieve('dialog-params');
    this.payment = this.navCtrl.getParam('payment') || params?.payment;
    if (this.isRightSideContent && this.payment) {
      this.rightSidePanelService.setDialogPageTitle(this.payment.payer_name);
    }
    if (this.payment) { this.buildPaymentLabels(this.payment); }
    setTimeout(() => {
      this.loaded = true;
      this.cdr.markForCheck();
    });
  }

  // `unit` already equals the room label for room payments (e.g. "Room A"); only append
  // `space_name` when it adds new information, so whole-unit payments render unchanged.
  buildPaymentLabels(payment: Payment): void {
    const base = `${payment.property_name} · ${payment.unit}`;
    this.propertyLabel = payment.space_name && payment.space_name !== payment.unit ? `${base} · ${payment.space_name}` : base;
    this.cadenceLabel = payment.cadence === 'weekly' ? 'Weekly' : payment.cadence === 'monthly' ? 'Monthly' : '';
    this.leaseLabel = payment.lease_id ? `Lease · ${payment.property_name}` : '';
    this.statusColorValue = this.statusColor(payment.status);
  }

  get payLink(): string {
    return this.payment ? this.rentService.payLink(this.payment) : '';
  }

  // What the owner nets, given who pays the processing fee.
  get ownerReceives(): number {
    if (!this.payment) { return 0; }
    return this.payment.fee_paid_by === 'owner'
      ? this.payment.amount - this.payment.fee
      : this.payment.amount;
  }

  get tenantPays(): number {
    if (!this.payment) { return 0; }
    return this.payment.fee_paid_by === 'tenant'
      ? this.payment.amount + this.payment.fee
      : this.payment.amount;
  }

  statusColor(status: PaymentStatus): string {
    switch (status) {
      case 'Paid': return 'var(--primary-button, #4295a3)';
      case 'Due': return '#F9A825';
      case 'Overdue': return '#C62828';
      case 'Failed': return '#C62828';
      default: return '#1E2125';
    }
  }

  copyLink(): void {
    try { navigator?.clipboard?.writeText(this.payLink); } catch { /* clipboard unavailable */ }
    this.util.showSuccess(`Payment link copied: ${  this.payLink}`);
  }

  sendViaMessages(): void {
    this.featureGate.gate();
  }

  sendReminder(): void {
    this.featureGate.gate();
  }

  openPayPage(): void {
    this.goToPage('pay-page', { payment: this.payment }, PayPage);
  }

  // Tenant payments are credited against their lease — deep-link to its ledger.
  goToLease(): void {
    if (!this.payment?.lease_id) { return; }
    this.goToPage('lease-ledger', { leaseId: this.payment.lease_id }, LeaseLedgerPage);
  }

  async recordOffline(): Promise<void> {
    this.featureGate.gate();
  }

  private closePanel(): void {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateBack('finance');
    }
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.cdr.markForCheck());
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
